<template>
  <div v-if="show" class="modal-overlay modal-overlay-elevated" @click.self="$emit('cancel')">
    <!-- 2FA Setup Dialog -->
    <div class="modal-content max-w-2xl" role="dialog" aria-modal="true" aria-labelledby="2fa-setup-title">
      <div class="modal-header">
        <div class="flex items-center">
          <div class="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-4">
            <svg class="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
          </div>
          <h3 class="text-section-header" id="2fa-setup-title">Enable Two-Factor Authentication</h3>
        </div>
        <button @click="$emit('cancel')" class="modal-close-button" aria-label="Close dialog">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="modal-body space-y-6">
        <!-- Step indicator -->
        <div class="flex items-center justify-center space-x-4 mb-6">
          <div class="flex items-center">
            <div class="w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-medium">1</div>
            <span class="ml-2 text-sm font-medium text-gray-900">Setup</span>
          </div>
          <div class="w-16 h-0.5 bg-gray-200"></div>
          <div class="flex items-center">
            <div class="w-8 h-8 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center text-sm font-medium">2</div>
            <span class="ml-2 text-sm text-gray-500">Verify</span>
          </div>
        </div>

        <div v-if="setupData" class="space-y-6">
          <!-- Instructions -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 class="text-sm font-medium text-blue-900 mb-2">📱 Setup Instructions</h4>
            <ol class="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>Install an authenticator app (Google Authenticator, Authy, etc.)</li>
              <li>Scan the QR code below with your authenticator app</li>
              <li>Or manually enter the secret key</li>
              <li>Save the backup codes in a secure location</li>
              <li>Click "Continue to Verify" to complete setup</li>
            </ol>
          </div>

          <!-- QR Code and Secret -->
          <div class="grid md:grid-cols-2 gap-6">
            <!-- QR Code -->
            <div class="bg-white border border-gray-200 rounded-lg p-6 text-center">
              <h4 class="text-sm font-medium text-gray-900 mb-4">Scan QR Code</h4>
              <div class="flex justify-center mb-4">
                <img 
                  :src="`data:image/png;base64,${setupData.qr_code_image}`" 
                  alt="2FA QR Code"
                  class="max-w-48 h-auto border border-gray-200 rounded-lg"
                />
              </div>
              <p class="text-xs text-gray-500">
                Scan this QR code with your authenticator app
              </p>
            </div>

            <!-- Manual Secret -->
            <div class="bg-white border border-gray-200 rounded-lg p-6">
              <h4 class="text-sm font-medium text-gray-900 mb-4">Manual Setup</h4>
              <div class="space-y-3">
                <div>
                  <label class="text-label">Secret Key:</label>
                  <div class="mt-1 bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <code class="text-sm font-mono text-gray-800 break-all">{{ setupData.secret }}</code>
                  </div>
                  <button 
                    @click="copySecret" 
                    class="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    📋 Copy Secret
                  </button>
                </div>
                
                <div>
                  <label class="text-label">Account:</label>
                  <p class="text-sm text-gray-600">{{ userEmail }}</p>
                </div>

                <div>
                  <label class="text-label">Issuer:</label>
                  <p class="text-sm text-gray-600">Meal-Mate</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Backup Codes -->
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h4 class="text-sm font-medium text-yellow-800 mb-3">🔐 Backup Codes</h4>
            <p class="text-sm text-yellow-700 mb-4">
              Save these backup codes in a secure location. You can use them to access your account if you lose your authenticator device.
            </p>
            
            <div class="grid grid-cols-2 gap-2 mb-4">
              <div v-for="(code, index) in setupData.backup_codes" :key="index" 
                   class="bg-white border border-yellow-300 rounded px-3 py-2">
                <code class="text-sm font-mono text-gray-800">{{ code }}</code>
              </div>
            </div>
            
            <div class="flex flex-wrap gap-2">
              <button 
                @click="copyBackupCodes" 
                class="btn-secondary text-sm px-3 py-2"
              >
                📋 Copy All Codes
              </button>
              <button 
                @click="downloadBackupCodes" 
                class="btn-secondary text-sm px-3 py-2"
              >
                💾 Download Codes
              </button>
            </div>
          </div>
        </div>

        <!-- Loading state -->
        <div v-else class="text-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p class="text-body text-gray-500">Generating QR code and backup codes...</p>
        </div>
      </div>

      <div class="modal-footer">
        <BaseButton variant="secondary" @click="$emit('cancel')" :disabled="loading">
          Cancel
        </BaseButton>
        <BaseButton 
          variant="primary" 
          @click="$emit('continue')" 
          :disabled="!setupData || loading"
          :loading="loading"
        >
          Continue to Verify
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseButton from '../BaseButton.vue'
import { useAuthStore } from '../../stores/auth'
import { useBodyScrollLock } from '../../composables/useBodyScrollLock'

export interface TwoFactorSetupData {
  secret: string
  qr_code_url: string
  qr_code_image: string
  backup_codes: string[]
}

const props = defineProps<{
  show: boolean
  setupData: TwoFactorSetupData | null
  loading?: boolean
}>()
useBodyScrollLock(() => props.show)

defineEmits<{
  (e: 'cancel'): void
  (e: 'continue'): void
}>()

const authStore = useAuthStore()

const userEmail = computed(() => {
  return authStore.currentUser?.email || 'your-email@example.com'
})

const copySecret = async () => {
  if (props.setupData?.secret) {
    try {
      await navigator.clipboard.writeText(props.setupData.secret)
      // You could add a toast notification here
      console.log('Secret copied to clipboard')
    } catch (err) {
      console.error('Failed to copy secret:', err)
    }
  }
}

const copyBackupCodes = async () => {
  if (props.setupData?.backup_codes) {
    try {
      const codesText = props.setupData.backup_codes.join('\n')
      await navigator.clipboard.writeText(codesText)
      console.log('Backup codes copied to clipboard')
    } catch (err) {
      console.error('Failed to copy backup codes:', err)
    }
  }
}

const downloadBackupCodes = () => {
  if (props.setupData?.backup_codes) {
    const codesText = `Meal-Mate Two-Factor Authentication Backup Codes\n\nGenerated: ${new Date().toLocaleString()}\nAccount: ${userEmail.value}\n\nBackup Codes:\n${props.setupData.backup_codes.map((code, i) => `${i + 1}. ${code}`).join('\n')}\n\nImportant:\n- Keep these codes in a secure location\n- Each code can only be used once\n- Use these codes if you lose access to your authenticator app\n- Generate new codes if you suspect they have been compromised`
    
    const blob = new Blob([codesText], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `meal-mate-2fa-backup-codes-${new Date().getTime()}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }
}
</script>
